import { useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { ErrorBox } from "@/components/Spinner";
import { Icon, IconButton } from "@/components/ui";
import { useRecentDeployments } from "@/hooks/useRecentDeployments";
import { useShellState } from "@/hooks/useShellState";
import { inflightCount } from "@/lib/deployments";

import { TaskDetail } from "./TaskDetail";
import { TaskList } from "./TaskList";

const HEIGHT_STEP = 32;

export function TaskBar() {
  const {
    taskPanelCollapsed,
    toggleTaskPanel,
    taskPanelHeight,
    setTaskPanelHeight,
    selectedTask,
  } = useShellState();
  const query = useRecentDeployments();
  const items = query.data?.items ?? [];
  const inflight = inflightCount(items);

  const drag = useRef<{ startY: number; startH: number } | null>(null);

  function onGripPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    drag.current = { startY: e.clientY, startH: taskPanelHeight };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onGripPointerMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    // Dragging up (smaller clientY) grows the panel.
    setTaskPanelHeight(d.startH + (d.startY - e.clientY));
  }
  function onGripPointerUp(e: React.PointerEvent) {
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }
  function onGripKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setTaskPanelHeight(taskPanelHeight + HEIGHT_STEP);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setTaskPanelHeight(taskPanelHeight - HEIGHT_STEP);
    }
  }

  const countBadge =
    inflight > 0 ? (
      <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-info-fg px-1.5 py-0.5 text-xs font-semibold text-on-fill">
        {inflight}
      </span>
    ) : null;

  if (taskPanelCollapsed) {
    return (
      <section className="shrink-0 border-t border-line bg-sunken">
        <button
          type="button"
          onClick={toggleTaskPanel}
          aria-expanded={false}
          className="flex h-9 w-full items-center gap-2 px-3 text-xs font-medium text-ink transition hover:bg-sunken-deep"
        >
          <Icon icon={ChevronUp} size="sm" />
          <span>Tasks</span>
          {countBadge}
          {inflight > 0 && (
            <span className="text-ink-muted">{inflight} in progress</span>
          )}
        </button>
      </section>
    );
  }

  return (
    <section className="flex shrink-0 flex-col border-t border-line bg-sunken">
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize task panel"
        tabIndex={0}
        onPointerDown={onGripPointerDown}
        onPointerMove={onGripPointerMove}
        onPointerUp={onGripPointerUp}
        onKeyDown={onGripKeyDown}
        className="group flex h-2 cursor-row-resize items-center justify-center hover:bg-sunken-deep focus-visible:bg-sunken-deep"
      >
        <span className="h-0.5 w-8 rounded-full bg-line group-hover:bg-line-field" />
      </div>

      <div className="flex h-8 shrink-0 items-center justify-between px-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          <span>Tasks</span>
          {countBadge}
          {query.isFetching && inflight > 0 && (
            <span className="font-normal normal-case text-ink-muted">
              refreshing…
            </span>
          )}
        </div>
        <IconButton
          icon={ChevronDown}
          label="Collapse task panel"
          size="sm"
          aria-expanded
          onClick={toggleTaskPanel}
        />
      </div>

      <div className="flex min-h-0" style={{ height: taskPanelHeight }}>
        <div className="min-h-0 flex-1 overflow-auto">
          {query.isError ? (
            <div className="p-3">
              <ErrorBox error={query.error} />
            </div>
          ) : (
            <TaskList items={items} />
          )}
        </div>
        {selectedTask && <TaskDetail task={selectedTask} />}
      </div>
    </section>
  );
}
